import React, { useState, useEffect } from 'react';
import './PharmacyBooking.css';
import { bookPharmacySlot, searchPharmacies, getAvailability, validateToken, bookHomeBloodAppointment } from '../../api/pharma';
import { RootState } from '../../store/store';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import ErrorDisplay from '../../components/BaseComponents/ErrorDisplay';
import { categorizeError, ErrorInfo } from '../../utils/errorHandler';
import { getProductType } from '../../utils/dataLayer';
import { ReactComponent as ArrowRightIcon } from '../../assets/icons/arrow-right.svg';
import { ReactComponent as CalendarIcon } from '../../assets/icons/CalendarIcon.svg';
import successIcon from '../../assets/icons/doneImg.png';
import appStore from '../../assets/icons/aapStore.png';
import googlePlay from '../../assets/icons/googlePlay.png';
import viewAvail from '../../assets/icons/viewAvail.png';
import PharmacyMap from './PharmacyMap';
import AddToCalendar from '../../components/BaseComponents/AddToCalendar';
import Loader from '../../components/BaseComponents/Loader';
import { ReactComponent as LocationIcon } from '../../assets/icons/Buildings1.svg';
import { ReactComponent as HomeIcon } from '../../assets/icons/Home1.svg';
import { ReactComponent as TickIcon } from '../../assets/icons/Tick.svg';
import { ReactComponent as CircleTick } from '../../assets/icons/CircleTick.svg';
import { ReactComponent as NoteTickIcon } from '../../assets/icons/NoteTickIcon.svg';
import { fromZonedTime } from 'date-fns-tz';

const UK_TIMEZONE = 'Europe/London';

type Pharmacy = {
  id: number;
  name: string;
  address1: string;
  city: string;
  postCode: string;
  phone: string;
  lon: number;
  lat: number;
};

type Availability = {
  date: string;
  times: string[];
};

const HOME_VISIT_MONTH_COUNT = 2;
const HOME_VISIT_MIN_DAYS_AHEAD = 4;
const HOME_VISIT_MAX_SLOTS_PER_DAY = 2;
const HOME_VISIT_TIME_SLOTS = [
  { label: '08:00-10:00', startHour: 8 },
  { label: '10:00-12:00', startHour: 10 },
  { label: '12:00-14:00', startHour: 12 },
  { label: '14:00-16:00', startHour: 14 },
  { label: '16:00-18:00', startHour: 16 },
  { label: '18:00-20:00', startHour: 18 },
];
const HOME_VISIT_CHOICE_LABELS = ['first choice', 'second choice', 'third choice'];
const FASTING_SLOT_NOTE =
  'Fasting is strongly recommended to ensure accurate results for certain biomarkers, including Glucose, Triglycerides, and LDL Cholesterol (calculated). We suggest choosing a time slot that accommodates this.';

type HomeVisitChoice = {
  date: string | null;
  timeSlotIndex: number | null;
};

const createEmptyHomeVisitChoices = (): HomeVisitChoice[] => [
  { date: null, timeSlotIndex: null },
  { date: null, timeSlotIndex: null },
  { date: null, timeSlotIndex: null },
];

const formatDateKey = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const generateHomeVisitDates = (): string[] => {
  const dates: string[] = [];
  const startDate = new Date();
  startDate.setHours(0, 0, 0, 0);
  startDate.setDate(startDate.getDate() + HOME_VISIT_MIN_DAYS_AHEAD);

  const endDate = new Date(startDate);
  endDate.setMonth(endDate.getMonth() + HOME_VISIT_MONTH_COUNT);

  const currentDate = new Date(startDate);
  while (currentDate < endDate) {
    dates.push(formatDateKey(currentDate));
    currentDate.setDate(currentDate.getDate() + 1);
  }

  return dates;
};

const formatDateForSelect = (dateKey: string) => {
  const [year, month, day] = dateKey.split('-');
  return `${day}/${month}/${year}`;
};

const buildIsoFromChoice = (dateKey: string, timeSlotIndex: number) => {
  const slot = HOME_VISIT_TIME_SLOTS[timeSlotIndex];
  const middleHour = slot.startHour + 1;
  const [year, month, day] = dateKey.split('-').map(Number);
  const ukDateTime = new Date(year, month - 1, day, middleHour, 0, 0, 0);
  return fromZonedTime(ukDateTime, UK_TIMEZONE).toISOString();
};

const getChoiceIso = (choice: HomeVisitChoice): string | null => {
  if (choice.date === null || choice.timeSlotIndex === null) {
    return null;
  }
  return buildIsoFromChoice(choice.date, choice.timeSlotIndex);
};

const isDuplicateSlot = (
  choices: HomeVisitChoice[],
  date: string,
  timeSlotIndex: number,
  excludeIndex: number,
) => choices.some((choice, index) => {
  if (index === excludeIndex) {
    return false;
  }
  return choice.date === date && choice.timeSlotIndex === timeSlotIndex;
});

const countSlotsOnDate = (
  choices: HomeVisitChoice[],
  date: string,
  excludeIndex?: number,
): number => choices.filter((choice, index) => {
  if (excludeIndex !== undefined && index === excludeIndex) {
    return false;
  }
  return choice.date === date && choice.timeSlotIndex !== null;
}).length;

const isDateAtMaxSlots = (
  choices: HomeVisitChoice[],
  date: string,
  excludeIndex: number,
): boolean => countSlotsOnDate(choices, date, excludeIndex) >= HOME_VISIT_MAX_SLOTS_PER_DAY;

const AppDownload = () => {
  return (
    <>
      <div className='download-app-container' >
        <div className='text-center' >
          <h3>Download the CoreVitals App</h3>
          <p>Track your results, view your history, and stay informed.</p>
        </div>
        <div className='download-app-buttons' >
          <a href="https://apps.apple.com/us/app/corevitals/id6746760510" target='_blank' rel='noreferrer'><img src={appStore} alt="" /></a>
          <a href="https://play.google.com/store/apps/details?id=me.corevitals.app&pli=1" target='_blank' rel='noreferrer'><img src={googlePlay} alt="" /></a>
        </div>
      </div>
    </>
  )
}

const AfterTest = () => {
  return (
    <div className='card-style white-card' >
      <h2>After the test</h2>
      <div className='font-14' >
        <div className='bullet top-space' >
          <div>You may resume your usual diet, normal supplements and medications unless told otherwise.</div>
        </div>
        <div className='bullet top-space' >
          <div>If you felt faint during the blood draw, rest and hydrate well post-appointment.</div>
        </div>
      </div>
    </div>
  )
}

const MenstrualCycle = () => {
  return (
    <div className='card-style white-card' >
      <h2>Menstrual Cycle & Hormone Testing (For Women)</h2>
      <div className='font-14' >
        <div className='bullet top-space' >
          <div>For <strong>baseline reproductive hormones</strong> (e.g. FSH, LH, estradiol): test <strong>between day 2–5</strong> of your menstrual cycle, ideally on <strong>day 3</strong>.</div>
        </div>
        <div className='bullet top-space' >
          <div>For <strong>progesterone or ovulation assessment</strong>: test around <strong>day 21</strong> of your cycle (or <strong>5–7 days</strong> after ovulation), assuming a <strong>28-day</strong> cycle. Also consider testing at this time for assessment of <strong>Oestrogen Dominance / Imbalances in the Oestrogen to Progesterone Ratio</strong>. </div>
        </div>
        <div className='bullet top-space' >
          <div>If you are <strong>not having regular periods</strong>, hormone tests can be done at any time.</div>
        </div>
      </div>
    </div>
  )
}

const DayOfTest = () => {
  return (
    <div className='card-style white-card' >
      <h2>Day of the test</h2>
      <div className='font-14' >
        <div className='bullet top-space' >
          <div><strong>Fast for 8–12 hours</strong>, unless otherwise instructed (water only). This is important for glucose, lipids, iron, insulin, and sometimes nutrient markers. Do not fast longer than 12 hours.</div>
        </div>
        <div className='bullet top-space' >
          <div>Stay <strong>well hydrated</strong> – drink water in the morning unless advised to be dry fasting.</div>
        </div>
        <div className='bullet top-space' >
          <div>Continue to avoid stress, exercise, food, and sexual activity that morning.</div>
        </div>
        <div className='bullet top-space' >
          <div>
            <div className='d-flex flex-column gap-10' >
              <div>If you're testing <strong>thyroid hormones</strong>:</div>
              <div className='d-flex gap-10'  >
                <span>a.</span>
                <div>If taking <strong>levothyroxine</strong>, you may take it <strong>before or after</strong> the test.</div>
              </div>
              <div className='d-flex gap-10'  >
                <span>b.</span>
                <div>If taking <strong>liothyronine (T3)</strong> or <strong>desiccated thyroid extract</strong>, take it <strong>after</strong> the test.</div>
              </div>
            </div>
          </div>
        </div>
        <div className='bullet top-space' >
          <div><strong>Avoid testing when acutely unwell</strong>, febrile, or under unusual stress unless the purpose is to investigate that state.</div>
        </div>
        <div className='bullet top-space' >
          <div>Wear <strong>loose clothing</strong> to allow easy access to your arm.</div>
        </div>
      </div>
    </div>
  )
}

const BeforeTest = () => {
  return (
    <div className='card-style white-card' >
      <h2>1-2 Days before your test</h2>
      <div className='font-14' >
        <div className='bullet top-space' >
          <div><strong>Avoid alcohol and caffeine</strong> for <strong>12–24 hours before</strong> your appointment.</div>
        </div>
        <div className='bullet top-space' ><div><strong>Avoid red meat and high-protein meals</strong> for <strong>24 hours before testing.</strong></div></div>
        <div className='bullet top-space' ><div><strong>Avoid strenuous or vigorous exercise</strong> for <strong>24–48 hours before testing.</strong></div></div>
        <div className='bullet top-space' ><div><strong>Avoid sexual activity, nipple stimulation, heavy meals, and stress</strong> for <strong>24–48 hours beforehand.</strong></div></div>
      </div>
    </div>
  )
}

type PrepItem = string | { label: string; text: string };

type PrepSection = {
  step?: number;
  title: string;
  note?: string;
  items: PrepItem[];
  footer?: string;
};

const PREP_SECTIONS: PrepSection[] = [
  {
    step: 1,
    title: '4 days before',
    items: [
      'Stop non-essential supplements, including vitamins, herbal remedies, protein powders, collagen and nootropics.',
      'Keep taking prescribed medication unless your doctor has told you otherwise.',
      'Biotin (vitamin B7) can affect hormone and thyroid results. If it was prescribed, speak to your doctor before pausing it.',
    ],
  },
  {
    step: 2,
    title: '1 to 2 days before',
    items: [
      'Avoid alcohol, caffeine, red meat, high protein or heavy meals, and strenuous exercise.',
      'Avoid sexual activity and nipple stimulation, and try to keep stress low.',
    ],
  },
  {
    step: 3,
    title: 'On the day of your test',
    note: 'Fast 8 to 12 hours | Water only | Loose sleeves',
    items: [
      'Don’t fast for longer than 12 hours, and drink plenty of water.',
      'Thyroid medication: levothyroxine can be taken before or after your test. Take liothyronine (T3) or desiccated thyroid extract after.',
      'If you’re acutely unwell or have a fever, consider rebooking.',
    ],
  },
  {
    title: 'Testing hormones? (for women)',
    items: [
      { label: 'FSH, LH and oestradiol:', text: 'day 2 to 5 of your cycle, ideally day 3' },
      { label: 'Progesterone:', text: 'around day 21 of a 28 day cycle (about 7 days after ovulation)' },
      { label: 'Irregular periods:', text: 'you can test at any time' },
    ],
    footer: 'Hormonal contraception and HRT affect hormone results. Speak to your GP before making any changes.',
  },
  {
    title: 'After your test',
    items: [
      'Resume your usual diet, supplements and medications, unless told otherwise.',
      'If you felt faint, rest and drink plenty of water.',
    ],
  },
];

const OurRecommendationGuide = ({ onBook }: { onBook: () => void }) => {
  return (
    <div className='parent-container our-recommendation'>
      <section className='our-recommendation-banner'>
        <h2>Our recommendation</h2>
        <p className='our-recommendation-lead'>
          A morning appointment between 8am-10am gives the most accurate results.
        </p>
        <p className='our-recommendation-copy'>
          Where you can, we recommend booking between 8am and 10am. Hormones, cortisol and iron naturally change through the day, so a morning blood draw gives the clearest picture.
        </p>
        <button type='button' className='our-recommendation-book' onClick={onBook}>
          BOOK MY TEST
        </button>
      </section>

      <h2 className='our-recommendation-heading'>How to prepare</h2>

      <div className='our-recommendation-steps'>
        {PREP_SECTIONS.map((section) => (
          <article key={section.title} className='our-recommendation-step'>
            <div className='our-recommendation-step-title'>
              {section.step != null && (
                <span className='our-recommendation-step-number'>{section.step}</span>
              )}
              <h3>{section.title}</h3>
            </div>
            {section.note && <p className='our-recommendation-step-note'>{section.note}</p>}
            <ul>
              {section.items.map((item) => {
                const key = typeof item === 'string' ? item : item.label;
                return (
                  <li key={key}>
                    {typeof item === 'string' ? (
                      item
                    ) : (
                      <>
                        <strong>{item.label}</strong> {item.text}
                      </>
                    )}
                  </li>
                );
              })}
            </ul>
            {section.footer && <p className='our-recommendation-step-footer'>{section.footer}</p>}
          </article>
        ))}
      </div>

      <button type='button' className='our-recommendation-morning' onClick={onBook}>
        BOOK MY TEST
      </button>
    </div>
  );
};

const FourDaysBeforeTest = () => {
  return (
    <div className='top-space' >
      <div className='card-style white-card' >
        <h2>4 Days before your test</h2>
        <div className='font-14' >
          <div className='bullet top-space' >
            <div><strong>Stop all non-essential supplements</strong> (including vitamins, minerals, herbal remedies, protein powders, collagen, and nootropics).</div>
          </div>
          <div className='bullet top-space' >
            <div><strong>Continue all prescription medications</strong> unless your doctor has advised you to stop.</div>
          </div>
          <div className='bullet top-space' >
            <div>If you are taking <strong>biotin (vitamin B7)</strong>, stop it <strong>48 hours before your test</strong>. It can interfere with certain hormone and thyroid results. If prescribed, speak with your doctor before pausing.</div>
          </div>
          <div className='top-space' >
            If you're on hormonal contraception or HRT, be aware that it can affect your hormone test results. For a true baseline of natural hormonal function, you'd need to be off hormonal contraception for several months prior. Speak to your GP if you're considering this option.
          </div>
        </div>
      </div>
    </div>
  )
}
const PharmacySearch: React.FC = () => {
  const [postcode, setPostcode] = useState<string>('');
  const [pharmacies, setPharmacies] = useState<Pharmacy[]>([]);
  const [selectedPharmacy, setSelectedPharmacy] = useState<Pharmacy | null>(null);
  const [hoveredPharmacy, setHoveredPharmacy] = useState<Pharmacy | null>(null);
  const [availability, setAvailability] = useState<Availability[]>([]);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [step, setStep] = useState<number>(0);
  const [bookingSuccess, setBookingSuccess] = useState<boolean>(false);
  const [appointmentId, setAppointmentId] = useState<number | null>(null);
  const [isBooking, setIsBooking] = useState<boolean>(false);
  const [collectionPreference, setCollectionPreference] = useState<string>('');
  const [showNextStep, setShowNextStep] = useState<boolean>(false);
  const [showHomeVisitScheduling, setShowHomeVisitScheduling] = useState<boolean>(false);
  const [homeVisitDates, setHomeVisitDates] = useState<string[]>([]);
  const [homeVisitChoices, setHomeVisitChoices] = useState<HomeVisitChoice[]>(createEmptyHomeVisitChoices());
  const [isBookingHomeVisit, setIsBookingHomeVisit] = useState<boolean>(false);
  const [errorInfo, setErrorInfo] = useState<ErrorInfo | null>(null);
  const [isTokenValidating, setIsTokenValidating] = useState<boolean>(true);
  const { memberData } = useSelector((state: RootState) => state.member);

  const paymentResult = new URLSearchParams(window.location.search).get('payment');

  useEffect(() => {
    const validateTokenOnLoad = async () => {
      if (paymentResult === 'success' || paymentResult === 'cancelled') {
        setIsTokenValidating(false);
        return;
      }
      const token = getTokenFromURL();
      if (token) {
        sessionStorage.setItem('temp_token', token);

        try {
          const response = await validateToken(token);

          if (response?.status !== 'success') {
            const errorInfo = categorizeError({
              response: {
                status: 403,
                data: { detail: "You don't have access to this form." }
              }
            });
            setErrorInfo(errorInfo);
            setIsTokenValidating(false);
            return;
          }
          setErrorInfo(null);
        } catch (error: any) {
          const categorizedError = categorizeError(error);
          if (
            categorizedError.type === 'access_restricted' ||
            categorizedError.type === 'network_error' ||
            categorizedError.type === 'session_expired'
          ) {
            setErrorInfo(categorizedError);
          } else {
            setErrorInfo(null);
          }
          setIsTokenValidating(false);
          return;
        }
      } else {
        const errorInfo = categorizeError({
          response: {
            status: 403,
            data: { detail: "You don't have access to this form." }
          }
        });
        setErrorInfo(errorInfo);
        setIsTokenValidating(false);
        return;
      }
      setIsTokenValidating(false);
    };

    validateTokenOnLoad();
  }, [paymentResult]);

  // Function to get token from URL
  const getTokenFromURL = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('token');
  };

  // Function to validate token and proceed to step 1
  const handleBookMyTest = async () => {
    const token = getTokenFromURL();
    window.dataLayer.push({
      event: 'pharmacy_booking_started',
      form_kind: 'blood_test_details',
      product_type: getProductType(window.location.pathname, memberData?.membership_type),
      page_type: 'blood_test'
    });

    if (!token) {
      toast.error('Ivalid URL');
      return;
    }

    try {
      const response = await validateToken(token);

      if (response?.status === 'success') {
        setStep(1);
        window.dataLayer.push({
          event: 'pharmacy_booking_done',
          form_kind: 'blood_test_details',
          product_type: getProductType(window.location.pathname, memberData?.membership_type),
          page_type: 'blood_test',
          member_details: memberData
        });
      } else {
        toast.error('Booking is already done or link is invalid');
      }
    } catch (error: any) {
      let errorMessage = 'Booking is alredy done or link is invalid';

      if (error.detail) {
        // Parse Tuli error if it exists
        const tuliErrorMatch = error.detail.match(/\(Tuli error: (.*?)\)/);
        if (tuliErrorMatch) {
          try {
            const tuliError = JSON.parse(tuliErrorMatch[1]);
            errorMessage = tuliError.error;
          } catch (e) {
            errorMessage = error.detail;
          }
        } else {
          errorMessage = error.detail;
        }
      }

      toast.error(errorMessage);
    }
  };

  const fetchPharmacies = async () => {
    if (!postcode) {
      alert('Please enter a postcode');
      return;
    }
    try {
      const response = await searchPharmacies(postcode);
      setPharmacies(response.pharmacies);
      setStep(2);
      window.dataLayer.push({
        event: 'pharmacy_searched',
        form_kind: 'blood_test_details',
        product_type: getProductType(window.location.pathname, memberData?.membership_type),
        page_type: 'blood_test',
        member_details: memberData
      });
    } catch (error: any) {
      let errorMessage = 'Failed to fetch pharmacies';

      if (error.detail) {
        // Parse Tuli error if it exists
        const tuliErrorMatch = error.detail.match(/\(Tuli error: (.*?)\)/);
        if (tuliErrorMatch) {
          try {
            const tuliError = JSON.parse(tuliErrorMatch[1]);
            errorMessage = tuliError.error;
          } catch (e) {
            errorMessage = error.detail;
          }
        } else {
          errorMessage = error.detail;
        }
      }

      toast.error(errorMessage);
    }
  };

  const fetchAvailability = async (pharmaId: number) => {
    try {
      const response = await getAvailability(pharmaId.toString());
      setAvailability(response.free_slots.availability);
      setSelectedDate(null); // Reset selected date when fetching new availability
      setSelectedTime(null); // Reset selected time
      setStep(3);
      window.dataLayer.push({
        event: 'pharmacy_availability_checked',
        form_kind: 'blood_test_details',
        product_type: getProductType(window.location.pathname, memberData?.membership_type),
        page_type: 'blood_test',
        member_details: memberData
      });
    } catch (error: any) {
      let errorMessage = 'Failed to fetch availability';

      if (error.detail) {
        // Parse Tuli error if it exists
        const tuliErrorMatch = error.detail.match(/\(Tuli error: (.*?)\)/);
        if (tuliErrorMatch) {
          try {
            const tuliError = JSON.parse(tuliErrorMatch[1]);
            errorMessage = tuliError.error;
          } catch (e) {
            errorMessage = error.detail;
          }
        } else {
          errorMessage = error.detail;
        }
      }

      toast.error(errorMessage);
    }
  };

  const bookSlot = async () => {
    if (!selectedPharmacy || !selectedTime) return;

    setIsBooking(true);
    try {
      const response = await bookPharmacySlot({
        pharmacy_id: selectedPharmacy.id.toString(),
        test_date: selectedTime
      });

      // Extract appointment ID from response
      const appointmentId = response?.appointment_id || response?.id || response?.booking_id;
      if (appointmentId) {
        setAppointmentId(appointmentId);
      }

      setBookingSuccess(true);
      sessionStorage.removeItem('temp_token');
      setStep(4); // Move to success step
    } catch (error: any) {
      let errorMessage = 'Failed to book slot';

      if (error.detail) {
        // Parse Tuli error if it exists
        const tuliErrorMatch = error.detail.match(/\(Tuli error: (.*?)\)/);
        if (tuliErrorMatch) {
          try {
            const tuliError = JSON.parse(tuliErrorMatch[1]);
            errorMessage = tuliError.error;
          } catch (e) {
            errorMessage = error.detail;
          }
        } else {
          errorMessage = error.detail;
        }
      }

      toast.error(errorMessage);
    } finally {
      setIsBooking(false);
    }
  };

  const resetHomeVisitSelection = () => {
    setHomeVisitChoices(createEmptyHomeVisitChoices());
  };

  const bookHomeVisit = async () => {
    const preferredDateTimes = homeVisitChoices.map(getChoiceIso);
    const [firstDateTime, secondDateTime, thirdDateTime] = preferredDateTimes;
    if (!firstDateTime || !secondDateTime || !thirdDateTime) {
      toast.error('Please select all three preferred date and time options');
      return;
    }

    const uniqueSlots = new Set(preferredDateTimes);
    if (uniqueSlots.size !== 3) {
      toast.error('Each choice must have a different date and time combination');
      return;
    }

    const slotsPerDate = homeVisitChoices.reduce<Record<string, number>>((acc, choice) => {
      if (choice.date !== null && choice.timeSlotIndex !== null) {
        acc[choice.date] = (acc[choice.date] ?? 0) + 1;
      }
      return acc;
    }, {});
    if (Object.values(slotsPerDate).some((count) => count > HOME_VISIT_MAX_SLOTS_PER_DAY)) {
      toast.error('You can select a maximum of two slots per day');
      return;
    }

    setIsBookingHomeVisit(true);
    try {
      await bookHomeBloodAppointment({
        firstDateTime,
        secondDateTime,
        thirdDateTime,
      });
      sessionStorage.removeItem('temp_token');
      setShowHomeVisitScheduling(false);
      setShowNextStep(true);
    } catch (error: any) {
      const errorMessage = error?.detail || 'Failed to book home visit';
      toast.error(errorMessage);
    } finally {
      setIsBookingHomeVisit(false);
    }
  };

  // Group times by period (morning, afternoon, evening)
  const groupTimesByPeriod = (times: string[]) => {
    return times.reduce((acc: { morning: string[], afternoon: string[], evening: string[] }, time) => {
      const hour = new Date(time).getHours();
      if (hour < 12) {
        acc.morning.push(time);
      } else if (hour < 17) {
        acc.afternoon.push(time);
      } else {
        acc.evening.push(time);
      }
      return acc;
    }, { morning: [], afternoon: [], evening: [] });
  };

  const renderAvailabilityPicker = ({
    title,
    subtitle,
    slots,
    onConfirm,
    confirmLabel = 'Confirm Booking',
    isConfirming = false,
  }: {
    title: React.ReactNode;
    subtitle: string;
    slots: Availability[];
    onConfirm: () => void;
    confirmLabel?: string;
    isConfirming?: boolean;
  }) => {
    const timesForDate = selectedDate
      ? slots.find((day) => day.date === selectedDate)?.times ?? []
      : [];

    const groupedTimes = groupTimesByPeriod(timesForDate);

    return (
      <div className='booking-container'>
        <div>
          <div className='box-header'>
            <h2>{title}</h2>
            <p>{subtitle}</p>
            <p className='sub-header-note'>{FASTING_SLOT_NOTE}</p>
          </div>

          <div className='date-buttons'>
            {slots.map((day) => (
              <button
                key={day.date}
                onClick={() => {
                  setSelectedDate(day.date);
                  setSelectedTime(null);
                }}
                className={`btn ${selectedDate === day.date ? 'active' : ''}`}
              >
                {new Date(day.date).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'short',
                })}
              </button>
            ))}
          </div>
        </div>

        {selectedDate && (
          <div>
            {groupedTimes.morning.length > 0 && (
              <div className='time-section'>
                <h4><span>Morning</span></h4>
                <div className='time-buttons'>
                  {groupedTimes.morning.map((time: string) => (
                    <button
                      key={time}
                      onClick={() => setSelectedTime(time)}
                      className={`btn ${selectedTime === time ? 'active' : ''}`}
                    >
                      {new Date(time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {groupedTimes.afternoon.length > 0 && (
              <div className='time-section'>
                <h4><span>Afternoon</span></h4>
                <div className='time-buttons'>
                  {groupedTimes.afternoon.map((time: string) => (
                    <button
                      key={time}
                      onClick={() => setSelectedTime(time)}
                      className={`btn ${selectedTime === time ? 'active' : ''}`}
                    >
                      {new Date(time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {groupedTimes.evening.length > 0 && (
              <div className='time-section'>
                <h4><span>Evening</span></h4>
                <div className='time-buttons'>
                  {groupedTimes.evening.map((time: string) => (
                    <button
                      key={time}
                      onClick={() => setSelectedTime(time)}
                      className={`btn ${selectedTime === time ? 'active' : ''}`}
                    >
                      {new Date(time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div style={{ marginTop: 20 }}>
          <button
            onClick={onConfirm}
            disabled={!selectedTime || isConfirming}
            className='form-button'
            style={{ position: 'relative', minHeight: '48px' }}
          >
            {isConfirming ? (
              <Loader height='18px' color='white' />
            ) : (
              confirmLabel
            )}
          </button>
        </div>
      </div>
    );
  };

  const renderHomeVisitPicker = () => {
    const allChoicesComplete = homeVisitChoices.every(
      (choice) => choice.date !== null && choice.timeSlotIndex !== null,
    );

    const updateChoice = (index: number, updates: Partial<HomeVisitChoice>) => {
      setHomeVisitChoices((prev) => {
        const next = [...prev];
        const updatedChoice = { ...next[index], ...updates };
        if (updates.date !== undefined && updates.timeSlotIndex === undefined) {
          updatedChoice.timeSlotIndex = null;
        }
        next[index] = updatedChoice;
        return next;
      });
    };

    return (
      <>
        <div className='sub-header'>
          <div className='sub-header-title'>Choose three time slots that work for you.</div>
          <div>We'll use these to schedule your home visit.</div>
          <div className='sub-header-note'>
            {FASTING_SLOT_NOTE}
          </div>
        </div>
        <div className='slots-wrapper'>
          {HOME_VISIT_CHOICE_LABELS.map((label, index) => {
            const choice = homeVisitChoices[index];

            return (
              <div key={label} className='booking-container slot-card'>
                <h3 className='slot-card-title'>{label}</h3>
                <div className='select-date-container'>
                  <select
                    className='select-date'
                    value={choice.date ?? ''}
                    onChange={(event) => updateChoice(index, {
                      date: event.target.value || null,
                      timeSlotIndex: null,
                    })}
                  >
                    <option value=''>Select a date</option>
                    {homeVisitDates.map((dateKey) => {
                      const isFullyBooked = isDateAtMaxSlots(homeVisitChoices, dateKey, index);
                      const isCurrentDate = choice.date === dateKey;

                      return (
                        <option
                          key={dateKey}
                          value={dateKey}
                          disabled={isFullyBooked && !isCurrentDate}
                        >
                          {formatDateForSelect(dateKey)}
                        </option>
                      );
                    })}
                  </select>
                </div>
                <div className='select-time'>
                  <h3 className='block-title-1'>Select a time</h3>
                  <div className='btn-group'>
                    {HOME_VISIT_TIME_SLOTS.map((slot, slotIndex) => {
                      const isActive = choice.timeSlotIndex === slotIndex;
                      const isDisabled = !choice.date
                        || isDuplicateSlot(homeVisitChoices, choice.date, slotIndex, index)
                        || (
                          choice.timeSlotIndex === null
                          && isDateAtMaxSlots(homeVisitChoices, choice.date, index)
                        );

                      return (
                        <button
                          key={slot.label}
                          type='button'
                          className={`btn ${isActive ? 'active' : ''}`}
                          disabled={isDisabled}
                          onClick={() => updateChoice(index, { timeSlotIndex: slotIndex })}
                        >
                          {slot.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div className='text-note'>* Please note all times are UK time (GMT/BST)</div>
        <div className='text-sub-note'>We've reserved this slot provisionally. The blood draw appointment will be confirmed once we verify the availability of our clinical team in your area.</div>
        <div className='form-button-wrap'>
          <button
            type='button'
            className='form-button'
            disabled={!allChoicesComplete || isBookingHomeVisit}
            onClick={bookHomeVisit}
            style={{ position: 'relative', minHeight: '48px' }}
          >
            {isBookingHomeVisit ? (
              <Loader height='18px' color='white' />
            ) : (
              'Confirm Booking'
            )}
          </button>
        </div>
      </>
    );
  };

  if (isTokenValidating) {
    return <Loader />;
  }

  if (paymentResult === 'success' || paymentResult === 'cancelled') {
    const paid = paymentResult === 'success';
    return (
      <div className='outer-wrapper' style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', width: '100%', padding: '40px 20px' }}>
        <div className='booking-success-container'>
          <div className='booking-container'>
            {paid && <img src={successIcon} alt="Payment received" />}
            <div className='box-header'>
              <h2>{paid ? 'Payment received' : 'Payment was not completed'}</h2>
              <p>
                {paid
                  ? 'Your member price has been paid. CoreVitals will be in touch about your test.'
                  : 'No charge was taken. Use the payment link in your email if you want to try again.'}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (errorInfo) {
    return (
      <div className='outer-wrapper' style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', width: '100%', padding: '40px 20px' }}>
        <ErrorDisplay errorInfo={errorInfo} />
      </div>
    );
  }

  return (
    <div className='outer-wrapper'>
      {step === 0 && (
        <OurRecommendationGuide onBook={handleBookMyTest} />
      )}
      {step === 1 && (
        <div>

          {!showNextStep && !showHomeVisitScheduling && (
            <div className='collection-prefernce' >
              <div className='card-style white-card' >
                <h2>Blood Collection Preference</h2>
                <div className='font-14' >Please choose how you'd like to provide your blood sample for testing.</div>
                <div className='radio-group-collection' >
                  <label className={`radio-label-collection ${collectionPreference === 'home' ? 'selected' : ''}`} >
                    <input
                      className='radio-input'
                      type="radio"
                      name="blood-collection-preference"
                      value="home"
                      checked={collectionPreference === 'home'}
                      onChange={(e) => setCollectionPreference(e.target.value)}
                    />
                    <HomeIcon />
                    <span>Have a nurse visit my home</span>

                    <span className='tick-icon' >{collectionPreference === 'home' && <TickIcon />}</span>
                  </label>
                  <label className={`radio-label-collection ${collectionPreference === 'location' ? 'selected' : ''}`} >
                    <input
                      className='radio-input'
                      type="radio"
                      name="blood-collection-preference"
                      value="location"
                      checked={collectionPreference === 'location'}
                      onChange={(e) => setCollectionPreference(e.target.value)}
                    />
                    <LocationIcon />
                    <span>Visit one of our test locations</span>
                    <span className='tick-icon' >{collectionPreference === 'location' && <TickIcon />}</span>
                  </label>
                </div>
                <div className='btn-wrapper' >
                  <button
                    className='button primary-button1'
                    onClick={async () => {
                      if (!collectionPreference) {
                        toast.error('Please select a collection preference');
                        return;
                      }

                      if (collectionPreference === 'home') {
                        setHomeVisitDates(generateHomeVisitDates());
                        resetHomeVisitSelection();
                        setShowHomeVisitScheduling(true);
                      } else {
                        setShowNextStep(true);
                      }
                    }}
                  >
                    NEXT
                  </button>
                </div>
              </div>


            </div>
          )}

          {showHomeVisitScheduling && collectionPreference === 'home' && (
            <div>
              <div className='booking-header'>
                <button
                  className='back-button'
                  onClick={() => {
                    setShowHomeVisitScheduling(false);
                    resetHomeVisitSelection();
                  }}
                >
                  <ArrowRightIcon />Back
                </button>
                <h2>Choose your appointment</h2>
                <div></div>
              </div>
              <div className='parent-container'>
                {renderHomeVisitPicker()}
              </div>
            </div>
          )}

          {showNextStep && collectionPreference === 'home' && (
            <div className='received-request' >
              <div className='parent-container' >
                <div className='card-style white-card received-request-card' >
                  <div className='text-center' >
                    <CircleTick />
                    <h2>We've Received Your Request</h2>
                    <p>Thank you for booking your home visit for CoreVitals</p>
                  </div>

                  <p className='top-space' >You will shortly receive a message from one of our nurses, who will be in touch to schedule your blood test appointment at a time that suits you. The nurse will perform your blood test in the comfort of your own home. They will bring everything they need for the appointment, but <a href='#blood-test-preparation-instructions' >read this guide</a> for how to get the most accurate results from your test. </p>
                </div>
                <AppDownload />

                <div id='blood-test-preparation-instructions' >
                  <div className='pharmacy-booking-header'>
                    <NoteTickIcon />
                    <h2 className='top-space' >Blood Test Preparation Instructions</h2>
                    <p>To get the most accurate and meaningful results from your blood test, please read the following instructions carefully before your appointment. This guide includes general preparation, time-of-day recommendations, and specific considerations for hormone, thyroid, metabolic, and nutrient-related tests. If you're tracking your menstrual cycle or testing reproductive hormones, please ensure you read the hormone-specific timing instructions.</p>
                  </div>
                  <div className='card-style card-style-2 text-left' >
                    <h2>When should I book my test?</h2>
                    <p className='top-space font-14' >We recommend booking your blood draw <strong>between 8am and 10am</strong>, as this timing provides the most reliable results for many sensitive biomarkers such as hormones, cortisol, iron, and fasting glucose.</p>
                    <p className='top-space font-14' >To get the most accurate and meaningful results from your blood test, please read the following instructions carefully before your appointment. This guide includes general preparation, time-of-day recommendations, and specific considerations for hormone, thyroid, metabolic, and nutrient-related tests. If you're tracking your menstrual cycle or testing reproductive hormones, please ensure you read the hormone-specific timing instructions.</p>
                  </div>
                  <FourDaysBeforeTest />
                  <BeforeTest />
                  <DayOfTest />
                  <MenstrualCycle />
                  <AfterTest />
                  <AppDownload />
                </div>
              </div>

            </div>
          )}

          {showNextStep && collectionPreference === 'location' && (
            <div className='search-pharmacy' >
              <div className='pharmacy-booking-header'>
                <h2>Let's get you booked</h2>
                <p>Now's the time to schedule your blood draw appointment.</p>
              </div>

              <div className="pharmacy-container">
                <h2>Search for a Pharmacy</h2>
                <input className="form-input" placeholder="Postcode" value={postcode} onChange={e => setPostcode(e.target.value)} />
                <button className="form-button" onClick={fetchPharmacies}>Search</button>
              </div>
            </div>
          )}
        </div>
      )}

      {step === 2 && (
        <>
          <div className='booking-header booking-header2'>
            <button className='back-button' onClick={() => setStep(1)}><ArrowRightIcon />Back</button>
            <h2>Select a location</h2>
            <div></div>
          </div>
          <div className="pharmacy-search-container">
            <div className="pharmacy-list-container">

              <div className='booking-container'>
                <div className='booking-header booking-header-mobile'>
                  <button className='back-button' onClick={() => setStep(1)}><ArrowRightIcon />Back</button>
                  <h2>Select a location</h2>
                  <div></div>
                </div>
                <ul className="pharmacy-list" >
                  {pharmacies.map((pharmacy: Pharmacy) => (
                    <li
                      key={pharmacy.id}
                      className={selectedPharmacy?.id === pharmacy.id ? 'selected' : ''}
                      onMouseEnter={() => setHoveredPharmacy(pharmacy)}
                      onMouseLeave={() => setHoveredPharmacy(null)}
                    >



                      <button className='pharmacy-list-item' onClick={() => {
                        setSelectedPharmacy(pharmacy);
                      }} >
                        <span>
                          <h5>{pharmacy.name}</h5>
                          <p>{pharmacy.address1}, {pharmacy.city}, {pharmacy.postCode}</p>
                          <p>Phone: {pharmacy.phone}</p>
                        </span>
                        <img src={viewAvail} width={24} height={24} alt="view availability" />
                      </button>
                      <div className='view-availability-button-container' >
                        <button
                          onClick={() => {

                            fetchAvailability(pharmacy.id);
                          }}
                          className='form-button view-availability-button'
                        >
                          View Availability
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
                <div className='view-availability-button-container placeholder-button' >
                  <button
                    className='form-button view-availability-button'
                  >
                    View Availability
                  </button>
                </div>
              </div>
            </div>
            <div className="pharmacy-map-container">
              {pharmacies.length > 0 && (
                <>
                  {/* <div style={{ marginBottom: '10px' }}>
                  Found {pharmacies.length} pharmacies
                </div> */}
                  <PharmacyMap
                    key={pharmacies.length > 0 ? pharmacies[0].id : 'empty'}
                    pharmacies={pharmacies}
                    selectedPharmacy={selectedPharmacy}
                    hoveredPharmacy={hoveredPharmacy}
                    onPharmacySelect={(pharmacy) => {
                      setSelectedPharmacy(pharmacy);
                      fetchAvailability(pharmacy.id);
                    }}
                  />
                </>
              )}
            </div>
          </div>
        </>
      )}

      {step === 3 && selectedPharmacy && (
        <div>
          <div className='booking-header' >
            <button className='back-button' onClick={() => setStep(2)}><ArrowRightIcon />Back</button>
            <h2>Choose your appointment</h2>
            <div></div>
          </div>


          {renderAvailabilityPicker({
            title: (
              <>
                Book Your Initial <span className='mobile-block'>Appointment at</span>{' '}
                <br className='mobile-hide' />
                {selectedPharmacy.name}
              </>
            ),
            subtitle: 'Choose a convenient date and time for your first visit.',
            slots: availability,
            onConfirm: bookSlot,
            isConfirming: isBooking,
          })}
        </div>
      )}

      {step === 4 && bookingSuccess && selectedPharmacy && selectedTime && (
        <div className='booking-success-container' >
          <div className='booking-container' >
            <img src={successIcon} alt="Success" />
            <div className='box-header' >
              <h2>You're booked!</h2>
              <p>Your appointment is confirmed for {" "}
                {new Date(selectedTime).toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "short"
                })},{" "}
                {new Date(selectedTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                {" "}at&nbsp;
                {selectedPharmacy.name}
              </p>
            </div>
            <div style={{ marginTop: '20px', textAlign: 'center' }}>
              <AddToCalendar
                appointment={{
                  title: `Appointment at ${selectedPharmacy.name}`,
                  description: `Your appointment is confirmed for ${new Date(selectedTime).toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "short"
                  })}, ${new Date(selectedTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} at ${selectedPharmacy.name}`,
                  startTime: selectedTime,
                  endTime: new Date(new Date(selectedTime).getTime() + 60 * 60 * 1000).toISOString(),
                  location: `${selectedPharmacy.address1}, ${selectedPharmacy.city}, ${selectedPharmacy.postCode}`,
                  appointmentId: selectedPharmacy.id
                }}
                manualAppointmentId={appointmentId || undefined}
              />
            </div>
            <div className='booking-info-card' >
              <div>
                <h5>
                  {selectedPharmacy.name}
                </h5>
                <p>
                  {selectedPharmacy.address1}, {selectedPharmacy.city}, {selectedPharmacy.postCode}
                </p>
                <p>tel. {selectedPharmacy.phone}</p>
              </div>
              <div>
                <div className='date-and-time' >
                  <CalendarIcon />
                  <span className='fw-bold'>{new Date(selectedTime).toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "short"
                  })},{" "}
                    {new Date(selectedTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                </div>
              </div>
            </div>
            {/* <div style={{ margin: '20px 0', padding: '20px', border: '1px solid #4CAF50', borderRadius: '5px' }}>
            <p>Your booking has been confirmed at:</p>
            <p><strong>Pharmacy:</strong> {selectedPharmacy.name}</p>
            <p><strong>Address:</strong> {selectedPharmacy.address1}, {selectedPharmacy.city}, {selectedPharmacy.postCode}</p>
            <p><strong>Date:</strong> {new Date(selectedTime).toLocaleDateString()}</p>
            <p><strong>Time:</strong> {new Date(selectedTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
          </div>
          */}
          </div>
          <AppDownload />

        </div>
      )}
    </div>
  );
};

export default PharmacySearch;